/**
 * Mapbox utility module
 * Initializes Mapbox GL JS with project configuration and shared utilities
 */

import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import { getPublicConfigValue } from './appConfig.js';

mapboxgl.accessToken = getPublicConfigValue('mapboxToken');

/**
 * Default map configuration
 */
export const defaultMapConfig = {
    style: 'mapbox://styles/mapbox/streets-v12',
    zoom: 14,
    interactive: true,
    scrollZoom: false,
    attributionControl: false,
    projection: 'mercator', // Flat map instead of 3D globe
};

/**
 * Avari bird logo marker SVG
 */
export const markerSvg = `
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_499_2)">
    <g filter="url(#filter0_d_499_2)">
    <path d="M32 56C45.2548 56 56 45.2548 56 32C56 18.7452 45.2548 8 32 8C18.7452 8 8 18.7452 8 32C8 45.2548 18.7452 56 32 56Z" fill="#291E03" fill-opacity="0.5"/>
    <path d="M32 56C45.2548 56 56 45.2548 56 32C56 18.7452 45.2548 8 32 8C18.7452 8 8 18.7452 8 32C8 45.2548 18.7452 56 32 56Z" stroke="#FCF8EE"/>
    <path d="M32 55.5C44.9787 55.5 55.5 44.9787 55.5 32C55.5 19.0213 44.9787 8.5 32 8.5C19.0213 8.5 8.5 19.0213 8.5 32C8.5 44.9787 19.0213 55.5 32 55.5Z" stroke="#FCF8EE"/>
    </g>
    <path d="M21.5537 24.1895C24.4365 21.4262 29.1289 21.4262 32.0117 24.1895L48.2363 39.7266H36.8359C35.4645 39.7266 34.1208 39.4756 32.8428 38.9658C31.5647 38.465 30.4263 37.7318 29.4561 36.8018L27.3281 34.7617C25.7799 33.2771 25.7798 30.8626 27.3281 29.3779C28.0838 28.6535 29.083 28.2598 30.1465 28.2598C31.2099 28.2599 32.2078 28.6536 32.9541 29.3779L42.0879 38.1338H44.2061L30.8271 25.3076C28.5973 23.1701 24.9582 23.17 22.7285 25.3076C21.6462 26.3451 21.0498 27.7412 21.0498 29.1992C21.0499 30.6568 21.6466 32.0338 22.7285 33.0713L29.6699 39.7266H27.3096L21.5537 34.208C20.4529 33.1527 19.7432 31.8286 19.4912 30.3887L17.1777 29.1094L19.5293 27.8125C19.8092 26.444 20.4995 25.1912 21.5537 24.1895ZM30.1465 29.8789C29.5216 29.8789 28.9521 30.1022 28.5137 30.5225C27.6181 31.3901 27.6181 32.7947 28.5137 33.6533L30.6406 35.6924C31.8722 36.873 33.4583 37.6693 35.1562 37.9912L29.6514 32.7139C29.325 32.3919 29.325 31.8911 29.6514 31.5781C29.9779 31.265 30.5006 31.265 30.8271 31.5781L37.6846 38.1523H39.7275L31.7793 30.5225C31.3409 30.1112 30.7714 29.879 30.1465 29.8789ZM24.5879 26.3438C25.1694 26.3438 25.6465 26.8202 25.6465 27.4004C25.6463 27.9806 25.1693 28.457 24.5879 28.457C24.0066 28.4569 23.5294 27.9805 23.5293 27.4004C23.5293 26.8203 24.0065 26.3439 24.5879 26.3438Z" fill="#FCF8EE"/>
    </g>
    <defs>
    <filter id="filter0_d_499_2" x="3.5" y="7.5" width="57" height="57" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
    <feOffset dy="4"/>
    <feGaussianBlur stdDeviation="2"/>
    <feComposite in2="hardAlpha" operator="out"/>
    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_499_2"/>
    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_499_2" result="shape"/>
    </filter>
    <clipPath id="clip0_499_2">
    <rect width="64" height="64" fill="white"/>
    </clipPath>
    </defs>
    </svg>
`;

/**
 * Create a custom Avari marker element
 * @returns {HTMLElement} Marker DOM element
 */
export function createMarkerElement() {
    const el = document.createElement('div');
    el.className = 'avari-map-marker';
    el.innerHTML = markerSvg;
    return el;
}

/**
 * Create a Mapbox map instance with Avari defaults
 * @param {HTMLElement} container - Map container element
 * @param {Object} options - Map options
 * @returns {mapboxgl.Map|null} Map instance or null if token not configured
 */
export function createMap(container, options = {}) {
    if (!mapboxgl.accessToken) {
        console.warn('Mapbox access token not configured');
        return null;
    }

    const config = {
        container,
        ...defaultMapConfig,
        ...options,
    };

    const map = new mapboxgl.Map(config);

    // Add navigation controls (zoom in/out)
    map.addControl(
        new mapboxgl.NavigationControl({
            showCompass: false,
        }),
        'top-right'
    );

    // Add attribution control in compact mode
    map.addControl(
        new mapboxgl.AttributionControl({
            compact: true,
        }),
        'bottom-left'
    );

    return map;
}

/**
 * Add an Avari marker to a map
 * @param {mapboxgl.Map} map - Map instance
 * @param {Array} lngLat - [longitude, latitude] coordinates
 * @returns {mapboxgl.Marker} Marker instance
 */
export function addMarker(map, lngLat) {
    const marker = new mapboxgl.Marker({
        element: createMarkerElement(),
        anchor: 'center',
    })
        .setLngLat(lngLat)
        .addTo(map);

    return marker;
}

export { mapboxgl };
export default mapboxgl;
